'use client'

import React from 'react'
import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', BROWN: '#8C5826', BORDER: '#ffe0c2', TEXT: '#1a1a1a', MUTED: '#6b7280' }

interface Props {
    currentPage: number
    totalPages: number
    baseUrl: string
}

const Pagination = ({ currentPage, totalPages, baseUrl }: Props) => {
    if (totalPages <= 1) return null

    const getPageUrl = (page: number) =>
        page <= 1 ? baseUrl : `${baseUrl}/page/${page}`

    const getPageNumbers = () => {
        const pages: (number | string)[] = []
        const showPages = 5

        if (totalPages <= showPages + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            pages.push(1)
            if (currentPage > 3) pages.push('...')

            const start = Math.max(2, currentPage - 1)
            const end = Math.min(totalPages - 1, currentPage + 1)

            for (let i = start; i <= end; i++) pages.push(i)

            if (currentPage < totalPages - 2) pages.push('...')
            pages.push(totalPages)
        }
        return pages
    }

    return (
        <div className="flex items-center justify-center gap-2 mt-12">
            <Link href={getPageUrl(currentPage > 1 ? currentPage - 1 : 1)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${currentPage > 1 ? 'hover:scale-110' : 'opacity-50 pointer-events-none'}`}
                style={{ background: '#ffffff', border: `1px solid ${C.BORDER}`, color: C.TEXT }}>
                <ChevronLeft size={18} />
            </Link>

            <div className="flex items-center gap-1">
                {getPageNumbers().map((page, i) => (
                    typeof page === 'number' ? (
                        <Link key={i} href={getPageUrl(page)}
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all hover:scale-110"
                            style={{
                                background: page === currentPage
                                    ? `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffb347 100%)`
                                    : '#ffffff',
                                color: page === currentPage ? C.BROWN : C.TEXT,
                                border: `1px solid ${page === currentPage ? 'transparent' : C.BORDER}`,
                                boxShadow: page === currentPage ? `0 4px 12px ${C.PRIMARY}40` : 'none'
                            }}>
                            {page}
                        </Link>
                    ) : (
                        <span key={i} className="w-10 h-10 flex items-center justify-center text-sm" style={{ color: C.MUTED }}>
                            {page}
                        </span>
                    )
                ))}
            </div>

            <Link href={getPageUrl(currentPage < totalPages ? currentPage + 1 : totalPages)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${currentPage < totalPages ? 'hover:scale-110' : 'opacity-50 pointer-events-none'}`}
                style={{ background: '#ffffff', border: `1px solid ${C.BORDER}`, color: C.TEXT }}>
                <ChevronRight size={18} />
            </Link>
        </div>
    )
}

export default Pagination
